import { useMemo } from "react";
import { Link } from "react-router-dom";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import { useSeo } from "@/hooks/useSeo";
import { site, stats } from "@/mocks/site";

const positions = [
  {
    icon: "ri-focus-3-line",
    title: "线索汇聚",
    text: "把分散在个人手里的材料集中起来，形成可检索、可追溯的公开记录，而不是让它停在私聊里。",
  },
  {
    icon: "ri-layout-masonry-line",
    title: "归档归类",
    text: "按内部消息、公众事件、身边事与维权线索等主题整理，同类问题放在一起，便于集中查看。",
  },
  {
    icon: "ri-refresh-line",
    title: "持续更新",
    text: "有新的进展或事实更正时，在对应入口同步，并保留修改记录，让每一条线索都有清晰的时间线。",
  },
];

const voices = [
  {
    name: "投稿人 · 王先生",
    role: "线索已归档",
    text: "以前遇到问题都不知道往哪说，写了也没人看。在这里提交后，至少能看到它被归类、被整理出来。",
  },
  {
    name: "读者 · 李女士",
    role: "关注社区议题",
    text: "分类很清楚，同一个小区的事能集中看到。更新的部分会标注出来，不用自己反复核对时间线。",
  },
  {
    name: "行业从业者 · 匿名",
    role: "提交内部材料",
    text: "整个流程不需要注册，也可以不留联系方式。材料提交后有明确的反馈周期，这点让人比较放心。",
  },
];

function AboutPage() {
  const jsonLd = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于我们 - 爆料平台",
        url: "https://gregbennettguitars.com.cn/about",
        inLanguage: "zh-CN",
        description: site.description,
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: site.name,
        email: site.email,
        url: "https://gregbennettguitars.com.cn/",
        description: site.tagline,
      },
    ],
    []
  );

  useSeo({
    title: "关于我们 - 平台定位、内容原则与联系方式 | 爆料平台",
    description:
      "了解爆料平台的定位、内容原则与运作方式：这是一个匿名爆料入口与线索汇聚中心，对内部消息、公众事件与维权内容进行归档归类，并持续更新核实进展。",
    keywords: "关于爆料平台,平台定位,内容原则,线索归档,联系方式",
    path: "/about",
    jsonLd,
  });

  return (
    <div className="w-full bg-background-50">
      <div className="border-b border-background-200 bg-background-100">
        <div className="w-full px-4 py-10 md:px-8 md:py-14">
          <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
          <h1 className="mt-4 max-w-3xl font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-5xl">
            一个把匿名材料整理清楚的线索中心
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            {site.description}
          </p>
        </div>
      </div>

      <section className="w-full">
        <div className="w-full px-4 py-12 md:px-8 md:py-16">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
            <div className="relative h-72 w-full overflow-hidden rounded-lg border border-background-200 bg-background-200 md:h-96">
              <img
                src="https://readdy.ai/api/search-image?query=abstract%20quiet%20editing%20studio%20with%20large%20pin%20board%20covered%20in%20blank%20paper%20cards%20and%20string%20lines%2C%20warm%20sepia%20and%20deep%20crimson%20accents%2C%20soft%20window%20light%2C%20editorial%20interior%20photography%2C%20no%20text%20no%20people&width=1000&height=800&seq=about-baoliao-01&orientation=landscape"
                alt="爆料平台线索整理与归档工作场景"
                title="爆料平台 - 线索汇聚与归档"
                className="h-full w-full object-top"
              />
            </div>
            <div>
              <span className="mb-3 inline-flex items-center gap-2 text-xs font-label font-medium tracking-[0.2em] text-primary-600">
                <span className="h-px w-6 bg-primary-400" />
                品牌介绍
              </span>
              <h2 className="font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-4xl">
                为什么会有这个平台
              </h2>
              <div className="mt-5 space-y-4 text-sm leading-loose text-foreground-700 md:text-base">
                <p>
                  很多有价值的材料，最后没能被任何人看到。原因往往不是它不重要，而是提交渠道太少、流程太复杂，或者投稿人不愿意暴露身份。
                </p>
                <p>
                  {site.name} 由此而来。它只做一件事：提供一个低门槛的匿名入口，把材料接住、归类、整理成公开可查的记录，并在有新进展时持续更新。
                </p>
                <p>
                  我们不替任何人下结论，也不代理任何一方的诉求。平台呈现的是投稿人陈述与公开资料的整理结果，把判断留给读者。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full border-y border-background-200 bg-background-100">
        <div className="w-full px-4 py-12 md:px-8 md:py-16">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-4xl">网站定位</h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            平台不是新闻媒体，也不是法律服务机构，而是一个面向公众的线索汇聚与归档中心。
          </p>
          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
            {positions.map((item) => (
              <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
                <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                  <i className={`${item.icon} text-xl`} />
                </span>
                <h3 className="font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full">
        <div className="w-full px-4 py-12 md:px-8 md:py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
            <div className="lg:col-span-5">
              <span className="mb-3 inline-flex items-center gap-2 text-xs font-label font-medium tracking-[0.2em] text-primary-600">
                <span className="h-px w-6 bg-primary-400" />
                内容原则
              </span>
              <h2 className="font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-4xl">
                我们坚持的几条底线
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                这些原则决定了哪些内容会公开、以什么方式公开，以及遇到争议时如何处理。
              </p>
            </div>
            <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:col-span-7">
              {site.principles.map((principle, index) => (
                <li
                  key={principle}
                  className="rounded-lg border border-background-200 bg-background-50 p-5"
                >
                  <span className="font-heading text-sm font-bold text-primary-600">
                    0{index + 1}
                  </span>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-700">{principle}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="w-full border-y border-background-200 bg-background-100">
        <div className="w-full px-4 py-12 md:px-8 md:py-16">
          <div className="grid grid-cols-2 gap-6 lg:grid-cols-4">
            {stats.map((item) => (
              <div key={item.label} className="border-l-2 border-accent-400 pl-4">
                <p className="text-xs font-label tracking-wide text-foreground-500 md:text-sm">
                  {item.label}
                </p>
                <p className="mt-1.5 flex items-baseline gap-1">
                  <span className="font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
                    {item.value}
                  </span>
                  <span className="text-xs text-foreground-500 md:text-sm">{item.suffix}</span>
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full">
        <div className="w-full px-4 py-12 md:px-8 md:py-16">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
            投稿人怎么说
          </h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            下面是部分投稿人与读者的反馈，均已做匿名处理。
          </p>
          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
            {voices.map((voice) => (
              <blockquote
                key={voice.name}
                className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-6"
              >
                <span className="flex h-8 w-8 items-center justify-center text-accent-500">
                  <i className="ri-double-quotes-l text-2xl" />
                </span>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">{voice.text}</p>
                <footer className="mt-5 border-t border-background-200 pt-4">
                  <p className="text-sm font-label font-medium text-foreground-950">{voice.name}</p>
                  <p className="mt-0.5 text-xs text-foreground-500">{voice.role}</p>
                </footer>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full border-t border-background-200 bg-background-100">
        <div className="w-full px-4 py-12 md:px-8 md:py-16">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                联系方式
              </h2>
              <p className="mt-3 text-sm text-foreground-600">
                合作咨询、更正申请与补充说明，都可以通过这些渠道联系到我们。
              </p>
              <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-foreground-700">
                <span className="flex items-center gap-2">
                  <i className="ri-mail-line text-primary-600" />
                  <span className="break-all">{site.email}</span>
                </span>
                <span className="flex items-center gap-2">
                  <i className="ri-inbox-archive-line text-primary-600" />
                  <span className="break-all">投稿专用：{site.tipEmail}</span>
                </span>
              </div>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-customer-service-2-line" />
                联系平台
              </Link>
              <Link
                to="/submit"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-3 text-sm font-label font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:bg-background-50 hover:text-primary-700"
              >
                <i className="ri-send-plane-line" />
                匿名爆料
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default AboutPage;